/**
 * Utility function to check if a coupon is expired
 * @param {string} expiryDate - Date string in format "DD-MM-YYYY" or "MM-DD-YYYY"
 * @returns {boolean} - true if coupon is expired, false otherwise
 */
export const isCouponExpired = (expiryDate) => {
  if (!expiryDate) return true; // If no expiry date, consider it expired for safety

  try {
    const parts = expiryDate.split('-').map(Number);
    
    if (parts.length !== 3 || parts.some(isNaN)) {
      return true; // Invalid date format, consider expired
    }

    const [first, second, year] = parts;
    let month, day;

    // Determine format intelligently:
    // - If first part > 12, it must be DD-MM-YYYY (day can be > 12, month cannot)
    // - If second part > 12, it must be MM-DD-YYYY (day can be > 12, month cannot)
    // - If both <= 12, try both formats and use the one that creates a valid date
    
    if (first > 12) {
      // DD-MM-YYYY format (e.g., "30-11-2025" -> day=30, month=11)
      day = first;
      month = second;
    } else if (second > 12) {
      // MM-DD-YYYY format (e.g., "11-30-2025" -> month=11, day=30)
      month = first;
      day = second;
    } else {
      // Ambiguous case (both <= 12): try MM-DD-YYYY first, then DD-MM-YYYY
      // Try MM-DD-YYYY format first
      month = first;
      day = second;
      let testDate = new Date(year, month - 1, day);
      
      // Check if date is valid
      if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day || testDate.getFullYear() !== year) {
        // Invalid with MM-DD-YYYY, try DD-MM-YYYY
        day = first;
        month = second;
        testDate = new Date(year, month - 1, day);
        
        // Check if this is valid
        if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day || testDate.getFullYear() !== year) {
          return true; // Both formats invalid, consider expired
        }
      }
    }

    // Validate month and day ranges
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return true; // Invalid date, consider expired
    }

    // Create date object (month is 0-indexed in JavaScript Date)
    const expiry = new Date(year, month - 1, day);
    
    // Validate the date was created correctly
    if (expiry.getMonth() !== month - 1 || expiry.getDate() !== day || expiry.getFullYear() !== year) {
      return true; // Invalid date, consider expired
    }

    const today = new Date();
    
    // Set time to start of day for accurate comparison (end of day for expiry)
    today.setHours(0, 0, 0, 0);
    expiry.setHours(23, 59, 59, 999); // Expiry date is valid until end of that day

    // Check if expiry date is before today
    return expiry < today;
  } catch (error) {
    console.error('Error parsing expiry date:', expiryDate, error);
    return true; // On error, consider expired for safety
  }
};

/**
 * Filter out expired coupons from an array of offers
 * @param {Array} offers - Array of offer objects with expiry_date property
 * @returns {Array} - Filtered array with only non-expired offers
 */
export const filterExpiredCoupons = (offers) => {
  if (!Array.isArray(offers)) return [];
  
  return offers.filter(offer => {
    if (!offer || !offer.expiry_date) return false;
    return !isCouponExpired(offer.expiry_date);
  });
};

/**
 * Filter expired coupons from restaurant data
 * @param {Object|Array} restaurantData - Restaurant object or array of restaurants
 * @returns {Object|Array} - Restaurant data with filtered offers
 */
export const filterRestaurantOffers = (restaurantData) => {
  if (Array.isArray(restaurantData)) {
    return restaurantData.map(restaurant => ({
      ...restaurant,
      offer: filterExpiredCoupons(restaurant?.offer || [])
    }));
  }
  
  if (restaurantData && typeof restaurantData === 'object') {
    return {
      ...restaurantData,
      offer: filterExpiredCoupons(restaurantData.offer || [])
    };
  }
  
  return restaurantData;
};
